<script setup>
import Button from '@/components/ui/button/Button.vue';
import Toaster from '@/components/ui/toast/Toaster.vue';
import { useToast } from '@/components/ui/toast/use-toast';
import { useDarkModeStore } from '@/stores/dark-mode';
import { usePage } from '@inertiajs/vue3';
import { LogOut } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch } from 'vue';

const { toast } = useToast();
const page = usePage();
const { changeThemeUser } = useDarkModeStore();
const { darkMode } = storeToRefs(useDarkModeStore());

// --- SLIDER AYARLARI ---
const activeIndex = ref(0);
let timer = null;

const slides = [
    {
        id: 1,
        image: '', // Resim yolunu kontrol edin
        title: 'Hiperaktivite nedir?',
        description: 'Çocuklarda dikkat eksikliği ve hiperaktivite bozukluğu hakkında kısa bir açıklama alanı.',
        btnText: 'Hemen Randevu Al',
        btnLink: '/randevu',
    },
    {
        id: 2,
        image: '',
        title: 'Uzman Kadromuz',
        description: 'Alanında deneyimli psikolog ve psikiyatristlerimizle tanışın.',
        btnText: 'Uzmanları İncele',
        btnLink: '/doktorlar',
    },
    {
        id: 3,
        image: '',
        title: 'Online Terapi',
        description: 'Evinizin konforunda uzmanlarımızla görüntülü görüşme yapabilirsiniz.',
        btnText: 'Detaylı Bilgi',
        btnLink: '/online',
    },
    {
        id: 4,
        image: '',
        title: 'Çocuk Gelişimi',
        description: 'Çocuğunuzun gelişim süreçlerini profesyonel destekle takip edin.',
        btnText: 'Bize Ulaşın',
        btnLink: '/iletisim',
    },
];

// 5 Saniyede bir geçiş
const startSlider = () => {
    timer = setInterval(() => {
        activeIndex.value = (activeIndex.value + 1) % slides.length;
    }, 5000);
};

const stopSlider = () => {
    clearInterval(timer);
};

const setSlide = (index) => {
    stopSlider();
    activeIndex.value = index;
    startSlider();
};

onMounted(() => {
    startSlider();
});

onUnmounted(() => {
    stopSlider();
});

// --- DİĞER FONKSİYONLAR ---
watch(
    () => ({ ...page.props.flash }),
    (value) => {
        if (value.error) toast({ variant: 'destructive', title: 'Hata', description: value.error });
        else if (value.success) toast({ variant: 'success', title: 'Başarılı', description: value.success });
    },
    { deep: true, immediate: true },
);

const logout = () => {
    window.axios.post('/logout').then(() => {
        window.location.href = '/login';
    });
};
</script>

<template>
    <Toaster />

    <div class="grid h-screen w-screen overflow-hidden lg:grid-cols-2">
        <div class="relative flex h-full flex-col items-center justify-center overflow-y-auto bg-background p-8">
            <div class="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[450px]">
                <slot />
            </div>
            <div class="absolute left-5 top-5"></div>
        </div>

        <div class="relative hidden h-full w-full flex-col overflow-hidden bg-zinc-900 lg:flex">
            <TransitionGroup name="fade" tag="div" class="relative h-full w-full">
                <div v-for="(slide, index) in slides" :key="slide.id" v-show="activeIndex === index" class="absolute inset-0 h-full w-full">
                    <img :src="slide.image" class="h-full w-full object-cover" alt="Slide" v-if="slide.image" />

                    <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                    <div class="absolute bottom-0 left-0 z-10 flex w-full flex-col items-start p-12 pb-24">
                        <h2 class="mb-4 text-3xl font-bold text-white drop-shadow-md">
                            {{ slide.title }}
                        </h2>
                        <p class="mb-8 max-w-lg text-lg text-gray-200 drop-shadow-sm">
                            {{ slide.description }}
                        </p>
                        <Button as-child variant="outline" class="border-none bg-white px-8 text-black hover:bg-gray-200">
                            <a :href="slide.btnLink">{{ slide.btnText }}</a>
                        </Button>
                    </div>
                </div>
            </TransitionGroup>

            <div class="absolute bottom-8 left-12 z-20 flex gap-2">
                <button v-for="(slide, index) in slides" :key="'dot-' + index" @click="setSlide(index)" class="h-1.5 rounded-full transition-all duration-300" :class="activeIndex === index ? 'w-10 bg-white' : 'w-4 bg-white/50 hover:bg-white/80'"></button>
            </div>

            <div class="absolute right-5 top-5 z-20 flex gap-2">
                <!--
 <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <Button variant="ghost" size="icon" class="bg-black/20 text-white hover:bg-black/40">
                            <Sun class="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                            <Moon class="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem @click="changeThemeUser('light')">Light</DropdownMenuItem>
                        <DropdownMenuItem @click="changeThemeUser('dark')">Dark</DropdownMenuItem>
                        <DropdownMenuItem @click="changeThemeUser('auto')">Auto</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu> 
-->

                <div v-if="page.props.auth.user">
                    <Button variant="ghost" size="icon" @click="logout" class="bg-black/20 text-white hover:bg-black/40">
                        <LogOut class="h-[1.2rem] w-[1.2rem]" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* GHOSTING ÇÖZÜMÜ İÇİN CSS
   absolute inset-0: Resimlerin üst üste binmesini sağlar.
   Geçiş sırasında önceki resim hemen kaybolmaz, opacity ile yumuşakça gider.
*/
.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Önemli: Geçiş anında öğelerin üst üste binmesini garantiler */
.fade-leave-active {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 0; /* Giden resim altta kalsın */
}

.fade-enter-active {
    z-index: 1; /* Gelen resim üstte olsun */
}
</style>
